import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { encodeOAuthState } from '@modules/integrationsV2/utils/encodeOAuthState';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import querystring from 'querystring';
import { ChangeEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { getSanitizedUrl } from 'v2/core/utils/sanitizeUrl';

const baseUrl = (subdomain: string) =>
  `https://${subdomain}.zendesk.com/oauth/authorizations/new`;

export const ZendeskSupportFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const { closeModal } = useMuiModalContext();

  const [zendeskSubDomain, setZendeskSubDomain] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');

  const handleTextInputChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setZendeskSubDomain(event.target.value);
  };

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidated,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.ZendeskSupport,
    integrationType,
    hasValidFormFields: !!zendeskSubDomain,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      const queryParams = {
        response_type: 'code',
        client_id: 'zdg-enterpret',
        scope: 'read',
        redirect_uri: `${window.location.origin}/settings/integrations`,
        state: encodeOAuthState({
          channelType: IntegrationChannelType.ZendeskSupport,
          direction: IntegrationDirection.Inbound,
          data: {
            subDomain: zendeskSubDomain,
            tenantSlug,
            description: descriptionInput.trim(),
          },
          integrationType,
        }),
      };

      const redirectZendeskUrl = getSanitizedUrl(
        `${baseUrl(zendeskSubDomain)}?${querystring.stringify(queryParams)}`,
      );

      const windowURL = window.open(redirectZendeskUrl, '_self');
      if (windowURL) {
        closeModal();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.ZendeskSupport,
        });
      }
    },
  });

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
        />
        <Stack p={3} spacing={4}>
          <TextField
            fullWidth
            required
            label="Zendesk Sub Domain"
            placeholder="Enter Sub Domain"
            onChange={handleTextInputChange}
            value={zendeskSubDomain}
          />
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidated={isPreValidated}
            integrationType={integrationType}
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText="Integrate"
        isDisabled={isDisabled}
        isLoading={false}
      />
    </Stack>
  );
};
